/**
 * Date formatting utilities for displaying timestamps to users.
 * All functions use browser timezone automatically.
 */

/**
 * Format an ISO timestamp as a date string.
 * For date-only strings (YYYY-MM-DD), uses UTC to prevent timezone shifts.
 * For datetime strings, uses local timezone.
 * @param iso - ISO 8601 timestamp string (e.g., "2024-12-05T16:32:00.000Z" or "2024-12-05")
 * @returns Formatted date string (e.g., "Dec 5, 2024") or empty string if invalid
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    // Check if it's a date-only string (YYYY-MM-DD format without time)
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(iso);

    if (isDateOnly) {
      // Parse as UTC to prevent timezone conversion
      return new Date(iso + 'T00:00:00Z').toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      });
    }

    // For datetime strings, use local timezone
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

/**
 * Format an ISO timestamp as a date and time string.
 * @param iso - ISO 8601 timestamp string (e.g., "2024-12-05T16:32:00.000Z")
 * @returns Formatted datetime string (e.g., "Dec 5, 2024, 10:32 AM") or empty string if invalid
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return iso;
  }
}
